<script setup>
import { nextTick, onBeforeUnmount, watch, ref } from 'vue';

const props = defineProps({
    modelValue: { type: String, default: '' },
    visible: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'close', 'send']);
const input = ref(null);

const focusComposer = () => nextTick(() => input.value?.focus());

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            focusComposer();
            document.addEventListener('keydown', handleEscape);
        } else {
            document.removeEventListener('keydown', handleEscape);
        }
    },
    { immediate: true }
);

onBeforeUnmount(() => document.removeEventListener('keydown', handleEscape));

function handleEscape(event) {
    if (event.key === 'Escape') {
        emit('close');
    }
}

function send() {
    if (!props.modelValue.trim() || props.disabled) {
        return;
    }

    emit('send');
}

function handleKeydown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
    }
}
</script>

<template>
    <Teleport to="body">
        <Transition name="flowie-handoff">
            <div
                v-if="visible"
                class="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-gray-950/30 backdrop-blur-[2px]"
                role="presentation"
                @click.self="emit('close')"
            >
                <section
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="flowie-handoff-title"
                    aria-describedby="flowie-handoff-description"
                    class="relative w-full max-w-2xl overflow-hidden bg-white border shadow-2xl rounded-2xl border-wlPrimary/20"
                >
                    <div
                        class="h-1 bg-gradient-to-r from-wlPrimary/50 via-wlPrimary to-wlPrimary/50"
                    ></div>
                    <div class="p-5 sm:p-6">
                        <div class="flex items-start gap-3 pr-9">
                            <div
                                class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-xl bg-wlPrimary/10 text-wlPrimary"
                            >
                                <svg
                                    class="w-5 h-5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.8"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                                    />
                                    <path
                                        d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z"
                                    />
                                </svg>
                            </div>
                            <div>
                                <h2
                                    id="flowie-handoff-title"
                                    class="text-base font-semibold text-gray-900 !mb-1"
                                >
                                    {{ _mft('dashboard:flowie.handoff.label') }}
                                </h2>
                                <p
                                    id="flowie-handoff-description"
                                    class="text-sm text-gray-500 !mb-0"
                                >
                                    {{
                                        _mft(
                                            'dashboard:flowie.handoff.description'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            class="absolute flex items-center justify-center w-8 h-8 text-gray-400 transition-colors rounded-lg top-5 right-5 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-wlPrimary"
                            :aria-label="_mft('dashboard:flowie.handoff.close')"
                            @click="emit('close')"
                        >
                            <svg
                                class="w-5 h-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>

                        <textarea
                            ref="input"
                            :value="modelValue"
                            :disabled="disabled"
                            :placeholder="placeholder"
                            rows="5"
                            class="w-full px-4 py-3 mt-5 text-sm text-gray-800 border border-gray-300 resize-none rounded-xl focus:border-wlPrimary focus:ring-2 focus:ring-wlPrimary/20 disabled:opacity-50"
                            @input="
                                emit('update:modelValue', $event.target.value)
                            "
                            @keydown="handleKeydown"
                        ></textarea>

                        <div
                            class="flex flex-col-reverse gap-2 mt-4 sm:flex-row sm:items-center sm:justify-between"
                        >
                            <button
                                type="button"
                                class="px-3 py-2 text-sm font-medium text-gray-600 transition-colors rounded-lg hover:text-gray-900 hover:bg-gray-100"
                                @click="emit('close')"
                            >
                                {{ _mft('dashboard:flowie.handoff.close') }}
                            </button>
                            <button-base
                                type="button"
                                style-type="primary"
                                :disabled="!modelValue.trim() || disabled"
                                class="justify-center sm:min-w-[8rem]"
                                @click="send"
                            >
                                {{ _mft('dashboard:flowie.handoff.send') }}
                                <template #icon>
                                    <svg
                                        class="w-5 h-5"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
                                        />
                                    </svg>
                                </template>
                            </button-base>
                        </div>
                    </div>
                </section>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.flowie-handoff-enter-active,
.flowie-handoff-leave-active {
    transition: opacity 180ms ease;
}

.flowie-handoff-enter-active section,
.flowie-handoff-leave-active section {
    transition: transform 180ms ease, opacity 180ms ease;
}

.flowie-handoff-enter-from,
.flowie-handoff-leave-to {
    opacity: 0;
}

.flowie-handoff-enter-from section,
.flowie-handoff-leave-to section {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
}
</style>
